import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import './TimeTracking.css';

const TimeTracking = ({ user }) => {
  const [isActive, setIsActive] = useState(false);
  const [activeLog, setActiveLog] = useState(null);
  const [timeLogs, setTimeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const currentMonth = new Date();
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    date: '',
    clock_in: '',
    clock_out: ''
  });
  const [submittingManual, setSubmittingManual] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  const fetchTimeLogs = useCallback(async () => {
    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const apiUrl = await getApiUrl(`/api/time/logs?year=${year}&month=${month}`);
      const response = await axios.get(apiUrl);
      setTimeLogs(response.data);
    } catch (error) {
      setError('Failed to load time logs');
      console.error('Error fetching time logs:', error);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchActiveSession();
    fetchTimeLogs();
  }, [selectedMonth, user, fetchTimeLogs]);

  const navigateMonth = (direction) => {
    const newMonth = new Date(selectedMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setSelectedMonth(newMonth);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const groupLogsByDay = (logs) => {
    const grouped = {};
    logs.forEach(log => {
      // Extract date from clock_in (format: DD/MM/YYYY HH:MM:SS)
      const dateOnly = log.clock_in ? log.clock_in.split(' ')[0] : log.date;

      if (!grouped[dateOnly]) {
        grouped[dateOnly] = [];
      }
      grouped[dateOnly].push(log);
    });
    return grouped;
  };

  const calculateDailyTotal = (logs) => {
    return logs.reduce((total, log) => total + (log.total_hours || 0), 0);
  };

  const calculateMonthlyTotal = (logs) => {
    return logs.reduce((total, log) => total + (log.total_hours || 0), 0);
  };

  const fetchActiveSession = async () => {
    try {
      const apiUrl = await getApiUrl('/api/time/active');
      const response = await axios.get(apiUrl);
      setIsActive(response.data.isActive);
      setActiveLog(response.data.activeLog);
    } catch (error) {
      console.error('Error fetching active session:', error);
      setError(`Failed to fetch active session: ${error.response?.data?.error || error.message}`);
      // If there's an authentication error, set isActive to false
      if (error.response?.status === 401 || error.response?.status === 403) {
        setIsActive(false);
        setActiveLog(null);
      }
    } finally {
      // Ensure loading is set to false even if there's an error
      setLoading(false);
    }
  };



  const handleClockIn = async () => {
    try {
      setClockLoading(true);
      setError('');
      setSuccess('');
      const apiUrl = await getApiUrl('/api/time/clock-in');
      const response = await axios.post(apiUrl);
      setSuccess('Clock in successful!');
      setIsActive(true);

      setActiveLog({
        id: response.data.logId,
        clock_in: response.data.clockIn,
        date: response.data.date
      });
      fetchTimeLogs();
    } catch (error) {
      console.error('Error clocking in:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to clock in';
      setError(`Clock in failed: ${errorMessage}`);
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setClockLoading(true);
      setError('');
      setSuccess('');
      const apiUrl = await getApiUrl('/api/time/clock-out');
      const response = await axios.post(apiUrl);
      setSuccess(`Clock out successful! Total hours: ${formatHoursToHHMM(response.data.totalHours)}`);
      setIsActive(false);
      setActiveLog(null);
      fetchTimeLogs();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to clock out');
      console.error('Error clocking out:', error);
    } finally {
      setClockLoading(false);
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    // Backend already provides data in DD/MM/YYYY HH:MM:SS format
    return dateTime;
  };

  const formatDate = (date) => {
    if (!date) return '';
    // Backend already provides date in DD/MM/YYYY format
    return date;
  };

  const getDateFromClockIn = (clockIn) => {
    if (!clockIn) return '';
    // Extract date part from DD/MM/YYYY HH:MM:SS format
    return clockIn.split(' ')[0];
  };



  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Convert decimal hours to HH:MM format
  const formatHoursToHHMM = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '00:00';

    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    try {
      setSubmittingManual(true);
      setError('');
      setSuccess('');

      const apiUrl = await getApiUrl('/api/time/manual-entry');
      const response = await axios.post(apiUrl, manualEntry);
      setSuccess(response.data.message);
      setShowManualEntry(false);
      setManualEntry({ date: '', clock_in: '', clock_out: '' });
      fetchTimeLogs();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create manual time entry');
      console.error('Error creating manual entry:', error);
    } finally {
      setSubmittingManual(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved">✓ Approved</span>;
      case 'pending':
        return <span className="status-badge pending">⏳ Pending</span>;
      case 'rejected':
        return <span className="status-badge rejected">✗ Rejected</span>;
      default:
        return <span className="status-badge approved">✓ Approved</span>;
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      setError('');

      // Calculate date range for the selected month
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
      const endDate = new Date(year, month, 0).toISOString().split('T')[0]; // Last day of month

      const apiUrl = await getApiUrl(`/api/time-export/export/${exportFormat}`);
      const response = await axios.post(apiUrl, {
        userId: user.id, // User's own logs
        startDate: startDate,
        endDate: endDate
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      const monthStr = month.toString().padStart(2, '0');
      const userStr = user.name.replace(/\s+/g, '_').toLowerCase();

      link.setAttribute('download', `user_${userStr}_${year}_${monthStr}.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(`Export completed successfully`);
    } catch (error) {
      setError('Failed to export time logs');
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };





  // Check if user has correct role for time tracking
  if (!user || !['director', 'operator', 'actor'].includes(user.user_type)) {
    return (
      <div className="time-tracking">
        <div className="error-message">
          Access denied. Time tracking is only available for dubbing roles (Director, Operator, Actor).
          Current user type: {user?.user_type || 'Not logged in'}
        </div>
      </div>
    );
  }

  return (
    <div className="time-tracking">
      <div className="time-tracking-header">
        <h2>Time Tracking - {user.name}</h2>
        <div className="current-time">
          Current Time: {getCurrentTime()}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <div className="clock-controls">
        <div className="status-indicator">
          <div className={`status-dot ${isActive ? 'active' : 'inactive'}`}></div>
          <span className="status-text">
            {isActive ? 'Currently Clocked In' : 'Currently Clocked Out'}
          </span>
        </div>

        <div className="clock-buttons">
          {!isActive ? (
            <button
              className="clock-in-btn"
              onClick={handleClockIn}
              disabled={clockLoading}
            >
              🕐 Clock In {clockLoading ? '(Loading...)' : ''}
            </button>
          ) : (
            <button
              className="clock-out-btn"
              onClick={handleClockOut}
              disabled={clockLoading}
            >
              🕐 Clock Out {clockLoading ? '(Loading...)' : ''}
            </button>
          )}

          {['director', 'operator', 'actor'].includes(user.user_type) && (
            <button
              className="manual-entry-btn"
              onClick={() => setShowManualEntry(!showManualEntry)}
            >
              📝 Manual Entry
            </button>
          )}
        </div>


        {activeLog && (
          <div className="active-session">
            <h3>Active Session</h3>
            <p><strong>Clock In:</strong> {formatDateTime(activeLog.clock_in)}</p>
            <p><strong>Date:</strong> {formatDate(activeLog.date)}</p>
          </div>
        )}
      </div>

      {showManualEntry && ['director', 'operator', 'actor'].includes(user.user_type) && (
        <div className="manual-entry-form">
          <h3>Manual Time Entry</h3>
          <form onSubmit={handleManualEntry}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Date (DD/MM/YYYY):</label>
                <input
                  type="text"
                  id="date"
                  value={manualEntry.date}
                  onChange={(e) => setManualEntry({ ...manualEntry, date: e.target.value })}
                  placeholder="DD/MM/YYYY"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="clock_in">Clock In (HH:MM):</label>
                <input
                  type="time"
                  id="clock_in"
                  value={manualEntry.clock_in}
                  onChange={(e) => setManualEntry({ ...manualEntry, clock_in: e.target.value })}
                  placeholder="HH:MM"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="clock_out">Clock Out (HH:MM):</label>
                <input
                  type="time"
                  id="clock_out"
                  value={manualEntry.clock_out}
                  onChange={(e) => setManualEntry({ ...manualEntry, clock_out: e.target.value })}
                  placeholder="HH:MM"
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" disabled={submittingManual}>
                {submittingManual ? 'Submitting...' : 'Submit Entry'}
              </button>
              <button type="button" onClick={() => setShowManualEntry(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="month-navigation">
        <button
          className="nav-btn"
          onClick={() => navigateMonth('prev')}
          disabled={selectedMonth <= new Date(2020, 0, 1)}
        >
          ← Previous Month
        </button>
        <h3 className="month-title">{formatMonthYear(selectedMonth)}</h3>
        <button
          className="nav-btn"
          onClick={() => navigateMonth('next')}
          disabled={selectedMonth >= currentMonth}
        >
          Next Month →
        </button>
      </div>

      <div className="export-section">
        <h3>Export Time Logs</h3>
        <div className="export-controls">
          <div className="export-format">
            <label>Format:</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
            </select>
          </div>
          <div className="export-info">
            <span>Exporting: {formatMonthYear(selectedMonth)}</span>
          </div>
          <button
            className="export-btn"
            onClick={handleExport}
            disabled={exportLoading || timeLogs.length === 0}
          >
            {exportLoading ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
          </button>
        </div>
        {timeLogs.length === 0 && (
          <div className="export-warning">
            No time logs available for export this month
          </div>
        )}
      </div>

      <div className="time-logs-section">
        <h3>Time Logs - {formatMonthYear(selectedMonth)}</h3>
        {loading ? (
          <div className="loading">Loading time logs...</div>
        ) : (
          <>
            <div className="monthly-summary">
              <div className="summary-card">
                <h4>Monthly Total</h4>
                <span className="total-hours">{formatHoursToHHMM(calculateMonthlyTotal(timeLogs))}</span>
              </div>
              <div className="summary-card">
                <h4>Days Worked</h4>
                <span className="days-count">{Object.keys(groupLogsByDay(timeLogs)).length} days</span>
              </div>
            </div>

            <div className="time-logs-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Daily Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(groupLogsByDay(timeLogs)).sort((a, b) => new Date(b) - new Date(a)).map((date) => {
                    const dayLogs = groupLogsByDay(timeLogs)[date];
                    const dailyTotal = calculateDailyTotal(dayLogs);

                    return dayLogs.map((log, index) => (
                      <tr key={log.id} className={log.clock_out ? 'completed' : 'active'}>
                        {index === 0 && (
                          <td rowSpan={dayLogs.length} className="date-cell">
                            {getDateFromClockIn(log.clock_in)}
                          </td>
                        )}
                        <td>{formatDateTime(log.clock_in)}</td>
                        <td>{log.clock_out ? formatDateTime(log.clock_out) : 'Active'}</td>
                        <td>{log.total_hours ? formatHoursToHHMM(log.total_hours) : '-'}</td>
                        <td>{getStatusBadge(log.status)}</td>
                        {index === 0 && (
                          <td rowSpan={dayLogs.length} className="daily-total">
                            <strong>{formatHoursToHHMM(dailyTotal)}</strong>
                          </td>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>

            {Object.keys(groupLogsByDay(timeLogs)).length === 0 && (
              <div className="no-logs">
                Sorry, no records
              </div>
            )}

            <div className="monthly-total-footer">
              <h4>Monthly Total: {formatHoursToHHMM(calculateMonthlyTotal(timeLogs))}</h4>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TimeTracking;
